import { useCallback, useEffect, useRef, useState } from 'react';
import {
  AlertTriangle,
  Check,
  Loader2,
  RotateCcw,
  Upload,
} from 'lucide-react';
import { supabase } from '../../../../Supabase/SupabaseClient';
import { convertToWebp, formatBytes, uploadImageToBucket } from '../../../../utils/imageUpload';
import { invalidarSiteAssets } from '../../../../utils/useSiteAssets';
import {
  GRUPOS,
  SITE_BUCKET,
  SITE_PREFIX,
  SLOTS_POR_CLAVE,
  construirRuta,
} from '../../../../config/siteAssets';
import styles from './Imagenes.module.css';

const META_POR_CLAVE = SLOTS_POR_CLAVE.reduce((acc, slot) => {
  acc[slot.clave] = { grupo: slot.grupo, orden: slot.orden ?? 0 };
  return acc;
}, {});

const PLACEHOLDER =
  'data:image/svg+xml;utf8,' +
  encodeURIComponent(
    `<svg xmlns="http://www.w3.org/2000/svg" width="800" height="600" viewBox="0 0 800 600"><rect width="100%" height="100%" fill="#f3f4f6"/><text x="50%" y="50%" fill="#9ca3af" font-family="Arial, sans-serif" font-size="20" dominant-baseline="middle" text-anchor="middle">Sin foto</text></svg>`
  );

const FALLBACK_HERO = '/images/hero.png';

const SLOTS_TOTAL = GRUPOS.reduce((sum, grupo) => sum + grupo.slots.length, 0);

function Imagenes() {
  const [assets, setAssets] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [uploadingKey, setUploadingKey] = useState(null);
  const [resettingKey, setResettingKey] = useState(null);
  const [summary, setSummary] = useState(null);
  const inputsRef = useRef({});
  const enviosRef = useRef({});

  const load = useCallback(async () => {
    const { data, error: loadError } = await supabase
      .from('site_assets')
      .select('id, clave, grupo, etiqueta, alt, url_foto, path, orden')
      .order('orden', { ascending: true });

    if (loadError) throw loadError;

    const mapa = {};
    (data || []).forEach((row) => {
      mapa[row.clave] = row;
    });
    setAssets(mapa);
  }, []);

  useEffect(() => {
    let activo = true;

    const init = async () => {
      setLoading(true);
      setError(null);
      try {
        await load();
      } catch (err) {
        if (activo) {
          setError(
            err.message ||
              'No se pudieron leer los slots. Ejecutá la migración 20260929020000_site_assets.sql en Supabase.'
          );
        }
      }
      if (activo) setLoading(false);
    };

    init();
    return () => {
      activo = false;
    };
  }, [load]);

  const pickFile = (clave) => inputsRef.current[clave]?.click();

  const handleFile = async (event, slot) => {
    const file = event.target.files?.[0];
    if (!file) return;

    if (enviosRef.current[slot.clave]) return;
    enviosRef.current[slot.clave] = true;

    setUploadingKey(slot.clave);
    setError(null);
    setSuccess(null);
    setSummary(null);

    let nuevoPath = null;
    let commit = false;

    try {
      const converted = await convertToWebp(file, { maxEdge: 2000 });
      const suffix = `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
      nuevoPath = construirRuta(slot.clave, `${suffix}-${converted.fileName}`);
      const url = await uploadImageToBucket(SITE_BUCKET, nuevoPath, converted.blob);

      const row = assets[slot.clave];
      const meta = META_POR_CLAVE[slot.clave] || {};
      const anterior = row?.path || null;

      const { error: writeError } = await supabase
        .from('site_assets')
        .upsert(
          {
            clave: slot.clave,
            grupo: row?.grupo || meta.grupo || 'otros',
            etiqueta: row?.etiqueta || slot.etiqueta,
            alt: row?.alt || slot.alt,
            url_foto: url,
            path: nuevoPath,
            orden: row?.orden ?? meta.orden ?? 0,
            updated_at: new Date().toISOString(),
          },
          { onConflict: 'clave' }
        );

      if (writeError) throw writeError;
      commit = true;

      if (anterior && anterior !== nuevoPath) {
        await supabase.storage.from(SITE_BUCKET).remove([anterior]);
      }

      await load();
      invalidarSiteAssets();
      setSummary({ before: converted.originalBytes, after: converted.bytes });
      setSuccess(`${slot.etiqueta}: foto actualizada.`);
    } catch (err) {
      if (!commit && nuevoPath) {
        await supabase.storage.from(SITE_BUCKET).remove([nuevoPath]);
      }
      setError(err.message || 'No se pudo subir la foto.');
    } finally {
      enviosRef.current[slot.clave] = false;
      setUploadingKey(null);
      const input = inputsRef.current[slot.clave];
      if (input) input.value = '';
    }
  };

  const handleReset = async (slot) => {
    const row = assets[slot.clave];
    if (!row?.url_foto) return;

    const confirmado = window.confirm(
      `¿Quitar la foto de "${slot.etiqueta}"? Se borra el archivo del bucket y el sitio vuelve a mostrar un placeholder.`
    );
    if (!confirmado) return;

    setResettingKey(slot.clave);
    setError(null);
    setSuccess(null);

    try {
      if (row.path) {
        await supabase.storage.from(SITE_BUCKET).remove([row.path]);
      }

      const { error: updateError } = await supabase
        .from('site_assets')
        .update({ url_foto: null, path: null, updated_at: new Date().toISOString() })
        .eq('clave', slot.clave);

      if (updateError) throw updateError;

      await load();
      invalidarSiteAssets();
      setSuccess(`${slot.etiqueta}: se quitó la foto.`);
    } catch (err) {
      setError(err.message || 'No se pudo quitar la foto.');
    } finally {
      setResettingKey(null);
    }
  };

  const handleAlt = async (slot, value) => {
    setError(null);
    try {
      const { error: updateError } = await supabase
        .from('site_assets')
        .update({ alt: value })
        .eq('clave', slot.clave);

      if (updateError) throw updateError;

      setAssets((prev) => ({
        ...prev,
        [slot.clave]: { ...prev[slot.clave], alt: value },
      }));
    } catch (err) {
      setError(err.message || 'No se pudo guardar la descripción.');
    }
  };

  if (loading) {
    return (
      <div className={styles.loading} role="status">
        <Loader2 className={styles.spinner} size={22} aria-hidden="true" />
        Cargando imágenes…
      </div>
    );
  }

  const sinSeed = Object.keys(assets).length === 0;

  return (
    <div className={styles.wrap}>
      <div className={styles.overview}>
        Las imágenes se guardan en el bucket <strong>{SITE_BUCKET}</strong> dentro de{' '}
        <strong>{SITE_PREFIX}/</strong> y se convierten a <strong>WebP</strong> automáticamente
        (lado mayor a 2000 px). Mientras un slot esté vacío, el sitio muestra un placeholder.
      </div>

      {error && (
        <div className={styles.errorBox} role="alert">
          <AlertTriangle size={18} aria-hidden="true" />
          <p>{error}</p>
        </div>
      )}

      {success && (
        <div className={styles.successBox} role="status">
          <Check size={18} aria-hidden="true" />
          <div>
            <p>{success}</p>
            {summary && (
              <p className={styles.summary}>
                {formatBytes(summary.before)} → {formatBytes(summary.after)} de peso
              </p>
            )}
          </div>
        </div>
      )}

      {sinSeed && (
        <div className={styles.errorBox} role="alert">
          <AlertTriangle size={18} aria-hidden="true" />
          <p>
            La tabla <strong>site_assets</strong> está vacía. Ejecutá la migración{' '}
            <code>20260929020000_site_assets.sql</code> en el SQL Editor de Supabase para crear
            los {SLOTS_TOTAL} slots editables.
          </p>
        </div>
      )}

      {GRUPOS.map((grupo) => {
        const conFoto = grupo.slots.filter((slot) => assets[slot.clave]?.url_foto).length;
        return (
          <section key={grupo.id} className={styles.grupo}>
            <header className={styles.grupoHeader}>
              <h2 className={styles.grupoTitulo}>{grupo.titulo}</h2>
              <span className={styles.grupoContador}>
                {conFoto}/{grupo.slots.length}
              </span>
              <p className={styles.grupoDesc}>{grupo.descripcion}</p>
            </header>

            <ul className={styles.grid}>
              {grupo.slots.map((slot) => {
                const row = assets[slot.clave];
                const url = row?.url_foto || null;
                const preview = url || (grupo.id === 'home' ? FALLBACK_HERO : PLACEHOLDER);
                const busy = uploadingKey === slot.clave;
                const resetting = resettingKey === slot.clave;

                return (
                  <li key={slot.clave} className={styles.card}>
                    <div className={styles.preview}>
                      <img
                        src={preview}
                        alt=""
                        loading="lazy"
                        className={url ? '' : styles.previewVacio}
                        onError={(e) => {
                          if (e.currentTarget.src !== PLACEHOLDER) {
                            e.currentTarget.onerror = null;
                            e.currentTarget.src = PLACEHOLDER;
                          }
                        }}
                      />
                      {!url && <span className={styles.badgeVacio}>Sin foto</span>}
                      {busy && (
                        <span className={styles.badgeCargando}>
                          <Loader2 className={styles.spinner} size={13} aria-hidden="true" />
                          Subiendo
                        </span>
                      )}
                    </div>

                    <div className={styles.cardBody}>
                      <span className={styles.cardTitle}>{slot.etiqueta}</span>
                      <code className={styles.cardKey}>{slot.clave}</code>

                      <label className={styles.altLabel} htmlFor={`alt-${slot.clave}`}>
                        Texto alternativo
                      </label>
                      <input
                        id={`alt-${slot.clave}`}
                        className={styles.input}
                        type="text"
                        defaultValue={row?.alt ?? slot.alt ?? ''}
                        onBlur={(e) => {
                          if (e.target.value !== (row?.alt ?? '')) handleAlt(slot, e.target.value);
                        }}
                        placeholder="Describí la foto para accesibilidad"
                      />

                      <div className={styles.cardActions}>
                        <input
                          ref={(el) => {
                            inputsRef.current[slot.clave] = el;
                          }}
                          className={styles.fileInput}
                          type="file"
                          accept="image/*"
                          onChange={(e) => handleFile(e, { ...slot, orden: row?.orden })}
                          disabled={busy}
                        />
                        <button
                          type="button"
                          className={styles.uploadButton}
                          onClick={() => pickFile(slot.clave)}
                          disabled={busy || resetting}
                        >
                          {busy ? (
                            <Loader2 className={styles.spinner} size={15} aria-hidden="true" />
                          ) : (
                            <Upload size={15} aria-hidden="true" />
                          )}
                          {busy ? 'Subiendo…' : url ? 'Reemplazar' : 'Subir'}
                        </button>

                        <button
                          type="button"
                          className={styles.resetButton}
                          onClick={() => handleReset(slot)}
                          disabled={!url || busy || resetting}
                          title="Volver a la imagen local"
                        >
                          {resetting ? (
                            <Loader2 className={styles.spinner} size={15} aria-hidden="true" />
                          ) : (
                            <RotateCcw size={15} aria-hidden="true" />
                          )}
                          <span className={styles.srOnly}>Quitar foto de {slot.etiqueta}</span>
                        </button>
                      </div>
                    </div>
                  </li>
                );
              })}
            </ul>
          </section>
        );
      })}
    </div>
  );
}

export default Imagenes;
